<script>
import { mapActions } from 'vuex'
import managementMixin from '@/modules/Management/managementMixin'

export default {
  name: 'ManagementBanners',
  components: {
    ContentHeader: () => import('@/components/general/ContentHeader'),
    Tabs: () => import('@/components/general/Tabs'),
    Action: () => import('@/components/general/Action'),
    FilterList: () => import('@/components/general/FilterList'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Datatable: () => import('@/components/general/Datatable'),
    Pagination: () => import('@/components/general/Pagination'),
    Dropdown: () => import('@/components/general/Dropdown'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm'),
    PageLimitSelector: () => import('@/components/general/PageLimitSelector')
  },

  mixins: [ managementMixin ],

  data () {
    return {
      bannersTabs: [
        {
          text: this.$t('management.banner.edit:tab.tools'),
          alias: 'tools'
        },
        {
          text: this.$t('management.banner.edit:tab.content'),
          alias: 'content'
        },
        {
          text: this.$t('management.banner.edit:tab.blog'),
          alias: 'blog'
        }
      ],

      pagination: {
        content: {
          query: {},
          filter: {
            location: 'content'
          },

          order: {},
          page: 1,
          limit: 12,
          lastPage: null
        },

        tools: {
          query: {},
          filter: {
            location: 'tools'
          },

          order: {},
          page: 1,
          limit: 12,
          lastPage: null
        },

        blog: {
          query: {},
          filter: {
            location: 'blog'
          },

          order: {},
          page: 1,
          limit: 12,
          lastPage: null
        }
      },

      modalRemoveOpen: false,
      activeTab: 'tools',
      bannerId: null
    }
  },

  computed: {
    bannersList () {
      return this.$store.getters.bannersList
    },

    canWrite () {
      return this.getContextPermission('banners') === 'write'
    }
  },

  watch: {
    'pagination.content.page' () {
      this.getBannersList()
    },

    'pagination.tools.page' () {
      this.getBannersList()
    },

    'pagination.blog.page' () {
      this.getBannersList()
    },

    'activeTab' () {
      this.getBannersList()
    }
  },

  created () {
    if (this.$route.params.activeTab) {
      this.activeTab = this.$route.params.activeTab
    }

    this.getBannersList()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetBannersList',
      'attemptToggleBannerActive',
      'attemptRemoveBanner'
    ]),

    createNewBanner () {
      this.$router.push({
        name: 'management.banners.edit',
        params: {
          location: this.activeTab
        }
      })
    },

    edit (id) {
      this.$router.push({
        name: 'management.banners.edit',
        params: {
          id: id,
          location: this.activeTab
        }
      })
    },

    searchBanner (search) {
      this.pagination[this.activeTab].query = {
        title: search
      }

      this.getBannersList()
    },

    getBannersList () {
      this.setFetching(true)

      this.attemptGetBannersList(this.pagination[this.activeTab]).then(pagination => {
        this.pagination[this.activeTab].lastPage = pagination.lastPage
      }).finally(() => {
        this.setFetching(false)
      })
    },

    onLimitChange (newLimit) {
      this.pagination[this.activeTab].limit = newLimit

      if (this.pagination[this.activeTab].page === 1) {
        this.getBannersList()
      } else {
        this.pagination[this.activeTab].page = 1
      }
    },

    dateFormat (datetime) {
      if (datetime === '' || datetime === undefined || datetime === null) {
        return datetime
      }

      const date = new Date(Date.parse(datetime))

      return Intl.DateTimeFormat('pt-BR').format(date)
    },

    prevPage () {
      if (this.pagination[this.activeTab].page > 1) {
        this.pagination[this.activeTab].page -= 1
      }
    },

    nextPage () {
      this.pagination[this.activeTab].page += 1
    },

    firstPage () {
      this.pagination[this.activeTab].page = 1
    },

    lastPage () {
      this.pagination[this.activeTab].page = this.pagination[this.activeTab].lastPage
    },

    toggleActive (bannerId, toggleFlag) {
      this.setFetching(true)

      this.attemptToggleBannerActive(bannerId).then(() => {
        this.getBannersList()

        if (!toggleFlag) {
          this.setFeedback({ message: this.$t('management.banner:feedback.toggle.activate.success') })
        } else {
          this.setFeedback({ message: this.$t('management.banner:feedback.toggle.deactivate.success') })
        }
      }).catch(() => {
        this.setFeedback({ message: this.$t('global.error') })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    remove () {
      this.modalRemoveOpen = false
      this.setFetching(true)

      this.attemptRemoveBanner(this.bannerId).then(() => {
        this.setFeedback({ message: this.$t('management.banner:feedback.remove.success') })
        this.getBannersList()
      }).catch(() => {
        this.setFeedback({ message: this.$t('management.banner:feedback.remove.error') })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    confirmRemove (bannerId) {
      this.bannerId = bannerId
      this.modalRemoveOpen = true
    },

    cancelRemove () {
      this.modalRemoveOpen = false
    },

    changeTab (alias) {
      this.activeTab = alias
    }

  }
}
</script>

<template>
  <div class="main-content management-banner-list">
    <ContentHeader
      :title="$t('management:header.title')"
      :description="$t('management:header.description')"
      background="/assets/images/themes/default/png/management_banner.png"
    >
      <Tabs
        slot="tabs"
        dark
        :links="availableTabLink"
      />
    </ContentHeader>
    <Tabs
      slot="button"
      class="center gamification-tabs"
      :links="bannersTabs"
      :alias-active="activeTab"
      use-alias
      @tabChange="changeTab"
    />

    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        type="button"
        primary
        large
        fixed-width
        :text="$t('management.banner:btn.add')"
        @click="createNewBanner"
      />

      <PageLimitSelector
        slot="order"
        :value="pagination[activeTab].limit"
        :storage-key="`page_limit_banners_${activeTab}`"
        @input="onLimitChange"
      />
      <FilterListSearch
        slot="search"
        hide-error-details
        @search="searchBanner"
      />
    </FilterList>
    <div class="center">
      <Datatable
        v-if="bannersList.length"
        :items="bannersList"
      >
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('management.banner.list:datatable.header.2') }}
            </th>
            <th
              class="th text-center"
              width="15%"
            >
              {{ $t('management.banner.list:datatable.header.5') }}
            </th>
            <th
              class="th text-center"
              width="30%"
            >
              {{ $t('management.banner.list:datatable.header.3') }}
            </th>
            <th
              class="th"
              width="120"
            >
              {{ $t('management.banner.list:datatable.header.4') }}
            </th>
            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <img
                class="image-cell"
                :src="props.item.image"
              >
            </td>
            <td class="td text-center">
              <span class="td-text">{{ props.item.order }}</span>
            </td>
            <td class="td text-center">
              <span class="td-text">{{ dateFormat(props.item.createdAt) }}</span>
            </td>
            <td class="td">
              <span
                v-if="props.item.active"
                class="td-tag active-ribbon"
              >{{ $t('management.list:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('management.list:inactive') }}</span>
            </td>
            <td
              v-if="canWrite"
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id)"
                />
                <DropdownLink
                  v-if="!props.item.active && canWrite"
                  :text="$t('global:activate')"
                  @click="toggleActive(props.item.id, props.item.active)"
                />
                <DropdownLink
                  v-else-if="props.item.active && canWrite"
                  :text="$t('global:deactivate')"
                  @click="toggleActive(props.item.id, props.item.active)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:remove')"
                  @click="confirmRemove(props.item.id)"
                />
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination[activeTab].page"
        :page-count="pagination[activeTab].lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>
    <ModalConfirm
      :active="modalRemoveOpen"
      :title="$t('management.banner:remove.title')"
      :description="$t('management.banner:remove.description')"
      @confirmAction="remove"
      @cancelAction="cancelRemove"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/management.css"></style>

<style lang="scss">
.management-banner-list {
  .gamification-tabs.center {
    display: block;
    margin: 20px auto;
    padding-top: 0;
    padding-bottom: 0;
  }
}
</style>
